import { PersonData, GeneralParameterDefinition, PersonalParameterDefinition } from '../types';

/**
 * Export data structure for parameters
 */
export interface ExportData {
  version: string;
  exportDate: string;
  generalParameters: Record<string, any>;
  personalParameters: {
    people: Array<{ id: string; name: string }>;
    parameterValues: Record<string, {
      parameterValues: Record<string, any>;
      careers: any[];
    }>;
  };
}

/**
 * Export parameters to JSON format
 */
export const exportParameters = (
  generalValues: Record<string, any>,
  people: PersonData[]
): void => {
  try {
    // Prepare export data structure
    const exportData: ExportData = {
      version: '2.0.0',
      exportDate: new Date().toISOString(),
      generalParameters: { ...generalValues },
      personalParameters: {
        people: people.map(p => ({ id: p.id, name: p.name })),
        parameterValues: {}
      }
    };

    // Process personal parameters
    people.forEach(person => {
      exportData.personalParameters.parameterValues[person.id] = {
        parameterValues: { ...person.parameterValues },
        careers: [...person.careers]
      };
    });

    // Convert to JSON
    const jsonString = JSON.stringify(exportData, null, 2);

    // Create and trigger download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wihe_parameters_${new Date().toISOString().split('T')[0]}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log('Parameters exported successfully');
  } catch (error) {
    console.error('Failed to export parameters:', error);
    alert('Error: Could not export parameters.');
  }
};

/**
 * Import parameters from JSON file
 */
export const importParameters = (
  file: File,
  onSuccess: (generalValues: Record<string, any>, people: PersonData[]) => void,
  onError: (error: string) => void
): void => {
  if (file.type !== 'application/json') {
    onError('Invalid file type. Please select a .json file exported from this application.');
    return;
  }

  const reader = new FileReader();
  
  reader.onload = (event) => {
    try {
      const importedData = JSON.parse(event.target?.result as string) as ExportData;
      
      // Basic validation
      if (!importedData || typeof importedData !== 'object') {
        throw new Error('Invalid JSON structure.');
      }
      
      if (!importedData.generalParameters || !importedData.personalParameters) {
        throw new Error('Missing required data sections.');
      }
      
      if (!importedData.personalParameters.people || !importedData.personalParameters.parameterValues) {
        throw new Error('Invalid personal parameters structure.');
      }
      
      // Validate people data
      const importedPeople = importedData.personalParameters.people;
      if (!Array.isArray(importedPeople) || importedPeople.length === 0) {
        throw new Error('No people data found in import file.');
      }
      
      // Limit to maximum people
      const maxPeople = 2;
      const limitedPeople = importedPeople.slice(0, maxPeople);
      
      // Reconstruct people data
      const reconstructedPeople: PersonData[] = limitedPeople.map(personInfo => {
        const personData = importedData.personalParameters.parameterValues[personInfo.id];
        
        if (!personData) {
          throw new Error(`Missing data for person: ${personInfo.name}`);
        }
        
        return {
          id: personInfo.id,
          name: personInfo.name,
          parameterValues: personData.parameterValues || {},
          careers: personData.careers || []
        };
      });
      
      // Validate careers structure
      reconstructedPeople.forEach(person => {
        person.careers.forEach(career => {
          if (!career.id || typeof career.startAge !== 'object' || typeof career.endAge !== 'object') {
            throw new Error(`Invalid career structure for person: ${person.name}`);
          }
        });
      });
      
      // Success - call the callback with raw data (validation happens in App.tsx)
      onSuccess(importedData.generalParameters, reconstructedPeople);
      
    } catch (error) {
      console.error('Error processing imported file:', error);
      onError(`Error importing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  
  reader.onerror = () => {
    console.error('Error reading file:', reader.error);
    onError('Error reading the selected file.');
  };
  
  reader.readAsText(file);
};

/**
 * Validate imported general parameters against expected structure
 */
export const validateGeneralParameters = (
  importedParams: Record<string, any>,
  parameterDefinitions: GeneralParameterDefinition[]
): Record<string, any> => {
  const validatedParams: Record<string, any> = {};
  
  parameterDefinitions.forEach(paramDef => {
    const importedValue = importedParams[paramDef.id];
    
    if (importedValue !== undefined) {
      if (paramDef.type === 'date' && typeof importedValue === 'string') {
        validatedParams[paramDef.id] = importedValue;
      } else if (paramDef.type === 'meanStdDev' && 
                 typeof importedValue === 'object' && 
                 importedValue !== null &&
                 'mean' in importedValue &&
                 'stdDev' in importedValue &&
                 typeof (importedValue as any).mean === 'number' && 
                 typeof (importedValue as any).stdDev === 'number') {
        validatedParams[paramDef.id] = {
          mean: (importedValue as any).mean,
          stdDev: Math.max(0, (importedValue as any).stdDev) // Ensure non-negative
        };
      }
    }
    
    // Use defaults if not found or invalid
    if (validatedParams[paramDef.id] === undefined) {
      if (paramDef.type === 'date') {
        validatedParams[paramDef.id] = paramDef.defaultValue || new Date().toISOString().split('T')[0];
        } else if (paramDef.type === 'meanStdDev') {
          validatedParams[paramDef.id] = {
            mean: paramDef.defaultMean || 0,
            stdDev: paramDef.defaultStdDev || 0,
            type: 'meanStdDev' as const
          };
        }
    }
  });
  
  return validatedParams;
};

/**
 * Validate imported personal parameters against expected structure
 */
export const validatePersonalParameters = (
  importedPeople: PersonData[],
  parameterDefinitions: PersonalParameterDefinition[]
): PersonData[] => {
  return importedPeople.map(person => {
    const validatedParams: Record<string, any> = {};
    
    parameterDefinitions.forEach(paramDef => {
      if (paramDef.type === 'calculated') {
        return; // Skip calculated fields
      }
      
      const importedValue = person.parameterValues[paramDef.id];
      
      if (importedValue !== undefined) {
        switch (paramDef.type) {
          case 'date':
            // Handle both string values and structured { value, type } objects
            if (typeof importedValue === 'string') {
              validatedParams[paramDef.id] = {
                value: importedValue,
                type: 'date' as const
              };
            } else if (typeof importedValue === 'object' && 
                       importedValue !== null && 
                       'value' in importedValue &&
                       typeof (importedValue as any).value === 'string') {
              validatedParams[paramDef.id] = {
                value: (importedValue as any).value,
                type: 'date' as const
              };
            }
            break;
          case 'number':
            // Handle both number values and structured { value, type } objects
            if (typeof importedValue === 'number') {
              validatedParams[paramDef.id] = {
                value: importedValue,
                type: 'number' as const
              };
            } else if (typeof importedValue === 'object' && 
                       importedValue !== null && 
                       'value' in importedValue &&
                       typeof (importedValue as any).value === 'number') {
              validatedParams[paramDef.id] = {
                value: (importedValue as any).value,
                type: 'number' as const
              };
            }
            break;
          case 'meanStdDev':
            if (typeof importedValue === 'object' && 
                importedValue !== null &&
                'mean' in importedValue &&
                'stdDev' in importedValue &&
                typeof (importedValue as any).mean === 'number' && 
                typeof (importedValue as any).stdDev === 'number') {
              validatedParams[paramDef.id] = {
                mean: (importedValue as any).mean,
                stdDev: Math.max(0, (importedValue as any).stdDev),
                type: 'meanStdDev' as const
              };
            }
            break;
        }
      }
      
      // Use defaults if not found or invalid
      if (validatedParams[paramDef.id] === undefined) {
        switch (paramDef.type) {
          case 'date':
            validatedParams[paramDef.id] = {
              value: paramDef.defaultValue || new Date().toISOString().split('T')[0],
              type: 'date' as const
            };
            break;
          case 'number':
            validatedParams[paramDef.id] = {
              value: paramDef.defaultValue || 0,
              type: 'number' as const
            };
            break;
          case 'meanStdDev':
            validatedParams[paramDef.id] = {
              mean: paramDef.defaultMean || 0,
              stdDev: paramDef.defaultStdDev || 0,
              type: 'meanStdDev' as const
            };
            break;
        }
      }
    });
    
    // Validate careers
    const validatedCareers = person.careers.map(career => {
      // Ensure all required career fields exist with proper structure
      const requiredFields = [
        'startAge', 'endAge', 'initialIncome',
        'annualIncomeIncrease', 'incomeInvestment', 'employerMatch', 'otherInvestment'
      ];
      
      const validatedCareer = { ...career };
      
      requiredFields.forEach(field => {
        const fieldValue = (validatedCareer as any)[field];
        if (!fieldValue || 
            typeof fieldValue !== 'object' ||
            typeof fieldValue.mean !== 'number' ||
            typeof fieldValue.stdDev !== 'number') {
          // Set default values
          (validatedCareer as any)[field] = { mean: 0, stdDev: 0 };
        } else {
          // Ensure stdDev is non-negative
          fieldValue.stdDev = Math.max(0, fieldValue.stdDev);
        }
      });
      
      // Ensure boolean fields exist
      validatedCareer.isStartNow = Boolean(validatedCareer.isStartNow);
      validatedCareer.isEndAtRetirement = Boolean(validatedCareer.isEndAtRetirement);
      validatedCareer.inflationAdjustedIncrease = Boolean(validatedCareer.inflationAdjustedIncrease);
      
      return validatedCareer;
    });
    
    return {
      ...person,
      parameterValues: validatedParams,
      careers: validatedCareers
    };
  });
};
